import { useEffect, useState } from 'react';
import { Drawer, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { useStores } from 'Hooks/useStore';
import { Course } from 'Services/Api/Courses/Types';
import styles from './CourseDetailsDrawer.module.scss';

export interface CourseDrawerItem {
	id: number;
	title: string;
	image: string;
	tags: string[];
	status?: 'Completed' | 'In Progress' | 'Not Started';
}

interface CourseDetailsDrawerProps {
	visible: boolean;
	course: CourseDrawerItem | null;
	onClose: () => void;
}

const certificationLabel = (course?: Course): string => {
	if (!course) return '';
	if (
		course.expiration_type === 'interval' &&
		course.expiration_interval_value
	) {
		const unit = course.expiration_interval_unit ?? '';
		const plural = course.expiration_interval_value > 1 ? 's' : '';
		return `${course.expiration_interval_value} ${unit}${plural}`;
	}
	if (course.expiration_type === 'date' && course.expiration_date) {
		return course.expiration_date;
	}
	return 'Never expires';
};

const CourseDetailsDrawer: React.FC<CourseDetailsDrawerProps> = observer(
	({ visible, course, onClose }) => {
		const { learningManagerStore } = useStores();
		const [loading, setLoading] = useState(false);

		useEffect(() => {
			if (!visible || !course) return;
			if (learningManagerStore.coursesById[course.id]) return;
			setLoading(true);
			learningManagerStore
				.fetchCourseById(course.id)
				.finally(() => setLoading(false));
		}, [visible, course?.id]);

		const detail = course
			? learningManagerStore.coursesById[course.id]
			: undefined;

		return (
			<Drawer
				className={styles.courseDetailsDrawer}
				placement="right"
				visible={visible}
				width={420}
				onClose={onClose}
				title={
					<div className={styles.drawerTitle}>
						<div className={styles.drawerTitle__name}>
							{detail?.name ?? course?.title ?? 'Course'}
						</div>
						<div className={styles.drawerTitle__badges}>
							{detail?.is_required && (
								<span
									className={classNames(
										styles.drawerTag,
										styles['drawerTag--ongoing']
									)}
								>
									Required
								</span>
							)}
							{course?.status && (
								<span
									className={classNames(styles.drawerTag, {
										[styles['drawerTag--completed']]:
											course.status === 'Completed',
										[styles['drawerTag--inProgress']]:
											course.status === 'In Progress',
										[styles['drawerTag--notStarted']]:
											course.status === 'Not Started'
									})}
								>
									{course.status}
								</span>
							)}
						</div>
					</div>
				}
			>
				{loading ? (
					<div className={styles.drawerLoading}>
						<Spin />
					</div>
				) : (
					<>
						<div className={styles.drawerSection}>
							<div className={styles.drawerSection__heading}>
								Details
							</div>
							<div className={styles.subSection}>
								<div className={styles.detailLine}>
									Course #: {course?.id}
								</div>
							</div>
						</div>

						{detail?.description && (
							<div className={styles.drawerSection}>
								<div className={styles.drawerSection__heading}>
									Description
								</div>
								<div className={styles.subSection}>
									<div className={styles.detailLine}>
										{detail.description}
									</div>
								</div>
							</div>
						)}

						<div className={styles.drawerSection}>
							<div className={styles.drawerSection__heading}>
								Certification
							</div>
							<div className={styles.subSection}>
								<div className={styles.detailLine}>
									Expiration: {certificationLabel(detail)}
								</div>
							</div>
						</div>
					</>
				)}
			</Drawer>
		);
	}
);

export default CourseDetailsDrawer;
